import { useState, useEffect, useContext } from "react";
import { getProjectsEligibleForDiscount } from "api/Projects";
import UserContext from "context/User";

interface EligibleProject {
  projectId: string;
  projectNumber: string;
  quoteId: string;
  quoteTotal: number;
  discountBase?: number;
  acceptedDate: string;
  invoiceId: string | null;
  billingEmail: string | null;
}

interface DiscountEligibilityResult {
  projects: EligibleProject[];
  isLoading: boolean;
  hasEligibleProjects: boolean;
  refetch: () => Promise<void>;
}

const useDiscountEligibility = (): DiscountEligibilityResult => {
  const { user } = useContext<any>(UserContext);
  const [projects, setProjects] = useState<EligibleProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEligibleProjects = async () => {
    // Only fetch if user is logged in and is a client
    if (!user?.id || !user?.client?.id) {
      setProjects([]);
      setIsLoading(false);
      return;
    }

    // Check if discount popup is explicitly disabled for the company
    // Default to enabled (true) if not set
    // Database stores as 0 (disabled) or 1 (enabled), but may be parsed as strings
    const popupSetting = user?.company?.data?.discount_popup;
    console.log('Discount popup setting:', popupSetting, 'Type:', typeof popupSetting);
    
    if (popupSetting === false || popupSetting === 0 || popupSetting === "0" || popupSetting === "false") {
      console.log('Discount popup is disabled, not fetching projects');
      setProjects([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('Fetching eligible projects for discount popup');
      const data = await getProjectsEligibleForDiscount();
      
      // The backend should filter by primaryClient, but we can double-check here
      const eligibleProjects: EligibleProject[] = data.map((project: any) => {
        return {
          projectId: project.id,
          projectNumber: project.sequence,
          quoteId: project.quoteId,
          quoteTotal: parseFloat(project.quoteTotal) || 0,
          discountBase: parseFloat(project.discountBase) || parseFloat(project.quoteTotal) || 0,
          acceptedDate: project.acceptedDate,
          invoiceId: project.invoiceId || null,
          billingEmail: project.billingEmail || null,
        };
      });

      setProjects(eligibleProjects);
    } catch (error: any) {
      console.error("Error fetching eligible projects:", error);
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEligibleProjects();
  }, [user?.id, user?.client?.id, user?.company?.data?.discount_popup]);

  return {
    projects,
    isLoading,
    hasEligibleProjects: projects.length > 0,
    refetch: fetchEligibleProjects,
  };
};

export default useDiscountEligibility;
